// Who raised this money document, who approved it, who archived it.
//
// docs/PARITY_2026-09-08.md §14: five routes compute these names on every read
// and no screen drew one, so "who changed that" — the question
// office/insight/Activity.js exists to answer — could not be answered at the
// point of use. `grep -rl created_by_name client/src` came back empty.
//
// ── The field names are MEASURED, not read off the SQL ──────────────────────
//
// Taken from a scratch server on 2026-09-09 (`node server/server.js` on 4131,
// then GET /api/estimates/1 and GET /api/invoices/2 with an admin token):
//
//   GET /estimates/:id   created_by_name  approved_by_name  accepted_by_name
//   GET /invoices/:id    created_by_name
//   both (i.* / e.*)     created_at  approved_at  accepted_at  archived_at
//
// Declared ONCE here and cross-checked against server/routes/estimates.js and
// server/routes/invoices.js by provenance.test.js. That is the shape CLAUDE.md
// prescribes: the same field names hardcoded inside two screens is how one of
// them ends up drawing a column the server stopped sending.
//
// ── NOT on the detail routes, and deliberately not invented here ────────────
//
// `archived_by_name` is selected by the estimates LIST only (routes/estimates.js
// joins `uar` there and not in the /:id handler), so an archived estimate's
// Record shows WHEN it was archived and not by whom. That gap needs a join
// added to a money route — Tier C, ships alone — and a screen guessing at the
// name would be worse than a screen that omits it.
//
// ── NO ARITHMETIC IN THIS FILE ──────────────────────────────────────────────
//
// Every entry is a person's name and a timestamp. Nothing here reads a total,
// subtotal, tax rate, discount, deposit, amount_paid or line item, and there
// is no operator on a number anywhere in it (CLAUDE.md rule 4). `item_count`
// is a count the parity entry also lists and is deliberately absent: it is a
// figure on a bill, and a count drawn beside money reads as arithmetic.

import React from 'react';
import { SectionLabel } from '../../../ui/fw';
import { fmtDateTime } from './format';

/**
 * The audit events a money document can carry, in the order they happen.
 *
 * `at` is the timestamp column and `by` the joined name. `byFallback` exists
 * for exactly one case, and it is a schema fact rather than a nicety:
 * server/database.js:911-912 records that `accepted_by` NULL means the CLIENT
 * accepted through the public link. So an acceptance with no user attached is
 * "by the client", not "by nobody" — the sentence EstimateDetail's own meta
 * line already uses.
 */
export const PROVENANCE_EVENTS = [
  { key: 'created', label: 'Raised', at: 'created_at', by: 'created_by_name' },
  { key: 'approved', label: 'Approved', at: 'approved_at', by: 'approved_by_name' },
  { key: 'accepted', label: 'Accepted', at: 'accepted_at', by: 'accepted_by_name', byFallback: 'the client' },
  { key: 'archived', label: 'Archived', at: 'archived_at', by: 'archived_by_name' },
];

/**
 * The audit trail of one document, as rows to draw.
 *
 * Gated on the TIMESTAMP, never on the name. An approval whose approver has
 * since been deleted still happened, and it has to keep saying so; whereas an
 * estimate with `approved_at` null was never approved, which is a different
 * statement from "approved by nobody". Getting that the wrong way round would
 * put an "Approved" row on every draft.
 *
 * An invoice shares the `invoices` table with estimates, so it carries
 * `approved_at` / `accepted_at` columns that are always null for it — filtering
 * on the timestamp is what makes one function correct for both without the
 * caller passing a kind.
 *
 * @returns {{key: string, label: string, at: string, name: string|null}[]}
 */
export function provenanceOf(doc) {
  if (!doc) return [];
  return PROVENANCE_EVENTS
    .filter((e) => doc[e.at])
    .map((e) => ({
      key: e.key,
      label: e.label,
      at: doc[e.at],
      name: doc[e.by] || e.byFallback || null,
    }));
}

/**
 * The Record card — the audit line, drawn.
 *
 * `of-facts` is an existing `auto-fit / minmax(min(150px, 100%), 1fr)` grid, so
 * it is one column at 320px and several on a desktop with no media query and no
 * fixed width of its own (docs/APP_REBUILD.md rules 1 and 2). Nothing is
 * introduced that a phone has to pan to reach.
 *
 * Returns null rather than an empty card when a document has no events yet: a
 * headed card with nothing under it reads as a failed request.
 */
export function ProvenanceCard({ doc }) {
  const rows = provenanceOf(doc);
  if (!rows.length) return null;
  return (
    <section className="of-card" data-provenance="record">
      <SectionLabel>Record</SectionLabel>
      <dl className="of-facts">
        {rows.map((r) => (
          <div key={r.key} data-provenance-event={r.key}>
            <dt>{r.label}</dt>
            <dd>
              {fmtDateTime(r.at)}
              {r.name ? <span className="of-cell__sub">by {r.name}</span> : null}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export default ProvenanceCard;
